// src/pages/admin/AdminReviewQueue.js
// Admin-only page: review pending profiles, flagged reviews, quality alerts.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, ExternalLink, ChevronDown, ChevronUp, AlertTriangle, Flag, Star } from 'lucide-react';
import { auth, db } from '../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { checkProfileCompleteness } from '../../utils/profileCompleteness';
import { trackEvent } from '../../utils/analytics';

const ADMIN_EMAIL = 'pac623@icloud.com';

const FLAG_REASON_LABELS = {
  inappropriate_content: 'Inappropriate content',
  not_related_to_services: 'Not related to services',
  factually_inaccurate: 'Factually inaccurate',
  suspected_fake: 'Suspected fake',
  spam_promotional: 'Spam / promotional',
  other: 'Other',
};

// ─── Completeness checklist ──────────────────────────────────────────────────

const CompletenessChecklist = ({ details }) => {
  const items = [
    { key: 'hasName', label: 'Name' },
    { key: 'hasPhoto', label: 'Photo' },
    { key: 'hasBio', label: 'Bio (20+ chars)' },
    { key: 'hasService', label: 'Service' },
    { key: 'hasAvailability', label: 'Availability' },
  ];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', marginTop: '8px' }}>
      {items.map(({ key, label }) => (
        <span key={key} style={{
          fontSize: '12px',
          color: details[key] ? '#059669' : '#DC2626',
          fontWeight: 500,
          fontFamily: "'Montserrat', sans-serif",
        }}>
          {details[key] ? '✓' : '✗'} {label}
        </span>
      ))}
    </div>
  );
};

// ─── Profile Review Card (from Session 1) ────────────────────────────────────

const ProfileReviewCard = ({ practitioner, onAction, actionLoading }) => {
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [expanded, setExpanded] = useState(false);

  const data = practitioner.data;
  const profile = data.website_published?.profile || data.website_draft?.profile || data.profile || {};
  const displayName = data.displayName || data.name || profile.displayName || profile.name || 'Unnamed';
  const bio = profile.bio || data.whyThisWork || '';
  const bioExcerpt = bio.length > 120 ? bio.slice(0, 120) + '...' : bio;
  const photo = profile.photoUrl || profile.photo || data.photoUrl || null;
  const services = data.website_published?.services || data.website_draft?.services || data.services || [];
  const activeServices = services.filter(s => s.active !== false);
  const slug = data.slug || null;
  const email = profile.email || data.email || '';
  const createdAt = data.createdAt?.toDate?.()
    ? data.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const { details } = checkProfileCompleteness(data);

  const handleReject = () => {
    if (!feedback.trim()) return;
    onAction(practitioner.id, 'approve_reject', 'reject', feedback.trim());
    setShowRejectInput(false);
    setFeedback('');
  };

  return (
    <div style={{
      background: 'white', border: '1px solid #E5E7EB', borderRadius: '12px',
      padding: '20px', marginBottom: '12px', fontFamily: "'Montserrat', sans-serif",
      opacity: actionLoading === practitioner.id ? 0.6 : 1, transition: 'opacity 0.2s ease',
    }}>
      <div style={{ display: 'flex', gap: '14px', marginBottom: '12px' }}>
        <div style={{
          width: '52px', height: '52px', borderRadius: '50%', flexShrink: 0,
          background: photo ? `url(${photo}) center/cover` : 'linear-gradient(135deg, #7c3aed, #22d3ee)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {!photo && (
            <span style={{ color: 'white', fontSize: '18px', fontWeight: 700 }}>
              {displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#111827', margin: '0 0 2px 0' }}>{displayName}</h3>
          <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>{email}{createdAt ? ` · Signed up ${createdAt}` : ''}</p>
        </div>
        <span role="button" tabIndex={0} onClick={() => setExpanded(!expanded)} onKeyDown={(e) => e.key === 'Enter' && setExpanded(!expanded)}
          style={{ cursor: 'pointer', userSelect: 'none', color: '#9CA3AF', padding: '4px' }}>
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </div>

      <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.5, margin: '0 0 8px 0' }}>
        {bioExcerpt || <em style={{ color: '#D1D5DB' }}>No bio provided</em>}
      </p>

      {activeServices.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
          {activeServices.slice(0, 5).map((s, i) => (
            <span key={i} style={{ padding: '2px 8px', background: '#f5f3ff', color: '#7c3aed', fontSize: '11px', fontWeight: 500, borderRadius: '12px' }}>
              {s.name || s.title || 'Service'}
            </span>
          ))}
          {activeServices.length > 5 && <span style={{ fontSize: '11px', color: '#9CA3AF' }}>+{activeServices.length - 5} more</span>}
        </div>
      )}

      <CompletenessChecklist details={details} />

      {expanded && (
        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #F3F4F6' }}>
          {bio && (
            <div style={{ marginBottom: '10px' }}>
              <p style={{ fontSize: '11px', fontWeight: 600, color: '#9CA3AF', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Full Bio</p>
              <p style={{ fontSize: '13px', color: '#374151', lineHeight: 1.6, margin: 0 }}>{bio}</p>
            </div>
          )}
          {activeServices.length > 0 && (
            <div>
              <p style={{ fontSize: '11px', fontWeight: 600, color: '#9CA3AF', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>All Services</p>
              {activeServices.map((s, i) => (
                <p key={i} style={{ fontSize: '13px', color: '#374151', margin: '0 0 2px 0' }}>
                  {s.name || s.title} {s.price ? `— $${s.price}` : ''} {s.duration ? `(${s.duration}min)` : ''}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
        {slug && (
          <a href={`/site/${slug}`} target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#7c3aed', fontWeight: 500, textDecoration: 'none' }}>
            View profile <ExternalLink size={12} />
          </a>
        )}
        <div style={{ flex: 1 }} />
        <button type="button" onClick={() => onAction(practitioner.id, 'approve_reject', 'approve')}
          disabled={actionLoading === practitioner.id}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '7px 16px', border: 'none', borderRadius: '8px', background: '#059669', color: 'white', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif" }}>
          <CheckCircle size={14} /> Approve
        </button>
        {!showRejectInput ? (
          <span role="button" tabIndex={0} onClick={() => setShowRejectInput(true)} onKeyDown={(e) => e.key === 'Enter' && setShowRejectInput(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '7px 16px', border: '1.5px solid #E5E7EB', borderRadius: '8px', background: 'white', color: '#DC2626', fontSize: '12px', fontWeight: 600, cursor: 'pointer', userSelect: 'none', fontFamily: "'Montserrat', sans-serif" }}>
            <XCircle size={14} /> Reject with feedback
          </span>
        ) : (
          <div style={{ display: 'flex', gap: '6px', flex: '1 1 200px' }}>
            <input type="text" value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Feedback for the practitioner..."
              style={{ flex: 1, padding: '6px 10px', border: '1.5px solid #FECACA', borderRadius: '6px', fontSize: '12px', fontFamily: "'Montserrat', sans-serif", outline: 'none', minWidth: '140px' }}
              onFocus={(e) => { e.target.style.borderColor = '#DC2626'; }}
              onBlur={(e) => { e.target.style.borderColor = '#FECACA'; }}
              onKeyDown={(e) => e.key === 'Enter' && handleReject()}
            />
            <button type="button" onClick={handleReject} disabled={!feedback.trim() || actionLoading === practitioner.id}
              style={{ padding: '6px 12px', border: 'none', borderRadius: '6px', background: feedback.trim() ? '#DC2626' : '#F3F4F6', color: feedback.trim() ? 'white' : '#9CA3AF', fontSize: '12px', fontWeight: 600, cursor: feedback.trim() ? 'pointer' : 'not-allowed', fontFamily: "'Montserrat', sans-serif", whiteSpace: 'nowrap' }}>
              Reject
            </button>
            <span role="button" tabIndex={0} onClick={() => { setShowRejectInput(false); setFeedback(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { setShowRejectInput(false); setFeedback(''); } }}
              style={{ fontSize: '12px', color: '#9CA3AF', cursor: 'pointer', userSelect: 'none', padding: '6px 4px' }}>
              Cancel
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Flagged Review Card ─────────────────────────────────────────────────────

const FlaggedReviewCard = ({ notification, onAction, actionLoading }) => {
  const [adminNote, setAdminNote] = useState('');
  const d = notification.data.details || {};
  const isLoading = actionLoading === notification.id;

  return (
    <div style={{
      background: 'white', border: '1px solid #E5E7EB', borderRadius: '12px',
      padding: '20px', marginBottom: '12px', fontFamily: "'Montserrat', sans-serif",
      opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.2s ease',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
        <Flag size={14} style={{ color: '#D97706' }} />
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
          Review on {notification.data.practitionerName}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        {[1, 2, 3, 4, 5].map(i => (
          <Star key={i} size={14} fill={i <= (d.reviewRating || 0) ? '#F59E0B' : '#E5E7EB'} stroke={i <= (d.reviewRating || 0) ? '#F59E0B' : '#E5E7EB'} />
        ))}
        <span style={{ fontSize: '12px', color: '#6B7280' }}>by "{d.reviewerName || 'Anonymous'}"</span>
      </div>

      <p style={{ fontSize: '13px', color: '#374151', lineHeight: 1.5, margin: '0 0 10px 0', fontStyle: 'italic' }}>
        "{d.reviewComment || 'No comment'}"
      </p>

      <div style={{ padding: '10px 12px', background: '#FEF3C7', borderRadius: '8px', marginBottom: '10px' }}>
        <p style={{ fontSize: '12px', fontWeight: 600, color: '#92400E', margin: '0 0 2px 0' }}>
          Flag reason: {FLAG_REASON_LABELS[d.flagReason] || d.flagReason}
        </p>
        {d.flagExplanation && (
          <p style={{ fontSize: '12px', color: '#92400E', margin: 0 }}>
            Practitioner says: "{d.flagExplanation}"
          </p>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <input type="text" value={adminNote} onChange={(e) => setAdminNote(e.target.value)} placeholder="Admin note (optional)..."
          style={{ flex: 1, padding: '6px 10px', border: '1.5px solid #D1D5DB', borderRadius: '6px', fontSize: '12px', fontFamily: "'Montserrat', sans-serif", outline: 'none' }}
          onFocus={(e) => { e.target.style.borderColor = '#7c3aed'; }}
          onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; }}
        />
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <span role="button" tabIndex={0}
          onClick={() => onAction(notification.id, 'flag_resolve', 'keep', d.reviewId, adminNote)}
          onKeyDown={(e) => e.key === 'Enter' && onAction(notification.id, 'flag_resolve', 'keep', d.reviewId, adminNote)}
          style={{ padding: '7px 16px', border: '1.5px solid #E5E7EB', borderRadius: '8px', background: 'white', color: '#374151', fontSize: '12px', fontWeight: 600, cursor: 'pointer', userSelect: 'none', fontFamily: "'Montserrat', sans-serif" }}>
          Keep review
        </span>
        <button type="button"
          onClick={() => onAction(notification.id, 'flag_resolve', 'remove', d.reviewId, adminNote)}
          disabled={isLoading}
          style={{ padding: '7px 16px', border: 'none', borderRadius: '8px', background: '#DC2626', color: 'white', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif" }}>
          Remove review
        </button>
      </div>
    </div>
  );
};

// ─── Quality Alert Card ──────────────────────────────────────────────────────

const QualityAlertCard = ({ notification, onDismiss, actionLoading }) => {
  const d = notification.data.details || {};
  const isWarning = notification.data.type === 'quality_warning';
  const isReview = notification.data.type === 'quality_review';
  const isExcessive = notification.data.type === 'excessive_flagging';
  const isLoading = actionLoading === notification.id;

  const icon = isReview ? '🚨' : isExcessive ? '🏴' : '⚠️';
  const label = isReview ? 'REVIEW NEEDED' : isExcessive ? 'Excessive Flagging' : 'WARNING';
  const borderColor = isReview ? '#FECACA' : isExcessive ? '#FDE68A' : '#FDE68A';

  return (
    <div style={{
      background: 'white', border: `1px solid ${borderColor}`, borderRadius: '12px',
      padding: '20px', marginBottom: '12px', fontFamily: "'Montserrat', sans-serif",
      opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.2s ease',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <span style={{ fontSize: '16px' }}>{icon}</span>
        <span style={{ fontSize: '13px', fontWeight: 700, color: isReview ? '#991B1B' : '#92400E' }}>
          {label}: {notification.data.practitionerName}
        </span>
      </div>

      {(isWarning || isReview) && (
        <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
          <span style={{ fontSize: '13px', color: '#374151' }}>
            Rating: <strong>{d.averageRating?.toFixed?.(1) || '?'}</strong>
          </span>
          <span style={{ fontSize: '13px', color: '#374151' }}>
            Reviews: <strong>{d.totalReviews || 0}</strong>
          </span>
        </div>
      )}

      <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.5, margin: '0 0 12px 0' }}>
        {d.message || 'Action may be needed.'}
      </p>

      <div style={{ display: 'flex', gap: '8px' }}>
        <span role="button" tabIndex={0}
          onClick={() => onDismiss(notification.id)}
          onKeyDown={(e) => e.key === 'Enter' && onDismiss(notification.id)}
          style={{ padding: '7px 16px', border: '1.5px solid #E5E7EB', borderRadius: '8px', background: 'white', color: '#374151', fontSize: '12px', fontWeight: 600, cursor: 'pointer', userSelect: 'none', fontFamily: "'Montserrat', sans-serif" }}>
          Mark reviewed
        </span>
      </div>
    </div>
  );
};

// ─── Main AdminReviewQueue page ──────────────────────────────────────────────

const AdminReviewQueue = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [practitioners, setPractitioners] = useState([]);
  const [flaggedNotifs, setFlaggedNotifs] = useState([]);
  const [qualityNotifs, setQualityNotifs] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Auth + admin check
  useEffect(() => {
    let done = false;
    const timeout = setTimeout(() => { if (!done) { done = true; setLoading(false); navigate('/dashboard/practitioner'); } }, 6000);

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (done) return;
      if (!user) return; // Firebase fires null first — wait for real user

      done = true;
      clearTimeout(timeout);

      if (user.email !== ADMIN_EMAIL) {
        navigate('/dashboard/practitioner');
        return;
      }

      setIsAdmin(true);
      await loadAllData();
      setLoading(false);
    });

    return () => { done = true; clearTimeout(timeout); unsub(); };
  }, [navigate]);

  const loadAllData = async () => {
    await Promise.all([
      loadPendingPractitioners(),
      loadFlaggedReviews(),
      loadQualityAlerts(),
    ]);
  };

  const loadPendingPractitioners = async () => {
    try {
      const q = query(collection(db, 'practitioners'), where('verification.manualReviewStatus', '==', 'pending'));
      const snap = await getDocs(q);
      const results = snap.docs.map(d => ({ id: d.id, data: d.data() }));
      results.sort((a, b) => {
        const aTime = a.data.createdAt?.toDate?.() || new Date(0);
        const bTime = b.data.createdAt?.toDate?.() || new Date(0);
        return bTime - aTime;
      });
      setPractitioners(results);
    } catch (err) {
      console.error('Failed to load pending practitioners:', err);
    }
  };

  const loadFlaggedReviews = async () => {
    try {
      const q = query(collection(db, 'adminNotifications'), where('type', '==', 'review_flagged'), where('status', '==', 'new'));
      const snap = await getDocs(q);
      setFlaggedNotifs(snap.docs.map(d => ({ id: d.id, data: d.data() })));
    } catch (err) {
      console.error('Failed to load flagged reviews:', err);
    }
  };

  const loadQualityAlerts = async () => {
    try {
      const types = ['quality_warning', 'quality_review', 'excessive_flagging'];
      const results = [];
      for (const type of types) {
        const q = query(collection(db, 'adminNotifications'), where('type', '==', type), where('status', '==', 'new'));
        const snap = await getDocs(q);
        snap.docs.forEach(d => results.push({ id: d.id, data: d.data() }));
      }
      setQualityNotifs(results);
    } catch (err) {
      console.error('Failed to load quality alerts:', err);
    }
  };

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const handleAction = async (id, actionType, action, extraId, note) => {
    setActionLoading(id);
    setSuccessMessage('');
    try {
      const functions = getFunctions(undefined, 'us-west1');

      if (actionType === 'approve_reject') {
        // Profile review
        const reviewFn = httpsCallable(functions, 'reviewPractitionerProfile');
        await reviewFn({ practitionerId: id, action, feedback: note || null });
        setPractitioners(prev => prev.filter(p => p.id !== id));
        if (action === 'approve') trackEvent('practitioner_approved', {});
        showSuccess(action === 'approve' ? 'Approved — practitioner is now live.' : 'Rejected — feedback sent.');

      } else if (actionType === 'flag_resolve') {
        // Flagged review resolution
        const resolveFn = httpsCallable(functions, 'resolveReviewFlag');
        await resolveFn({ reviewId: extraId, action, adminNote: note || null });
        setFlaggedNotifs(prev => prev.filter(n => n.id !== id));
        showSuccess(action === 'remove' ? 'Review removed from profile.' : 'Review kept — practitioner notified.');
      }
    } catch (err) {
      console.error('Action failed:', err);
      alert(`Failed: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismissAlert = async (notifId) => {
    setActionLoading(notifId);
    try {
      await updateDoc(doc(db, 'adminNotifications', notifId), {
        status: 'resolved',
        resolvedAt: new Date().toISOString(),
        resolvedBy: ADMIN_EMAIL,
      });
      setQualityNotifs(prev => prev.filter(n => n.id !== notifId));
      showSuccess('Alert dismissed.');
    } catch (err) {
      console.error('Dismiss failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gray-50 pt-20" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <div className="max-w-3xl mx-auto px-4 py-6">

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <p style={{ fontSize: '12px', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
            DF Admin
          </p>
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>
            Review Queue
          </h1>
          <p style={{ fontSize: '14px', color: '#6B7280' }}>
            Practitioner profiles, flagged reviews, and quality alerts.
          </p>
        </div>

        {/* Success toast */}
        {successMessage && (
          <div style={{
            padding: '10px 16px', background: '#D1FAE5', border: '1px solid #6EE7B7',
            borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#065F46', fontWeight: 500,
          }}>
            {successMessage}
          </div>
        )}

        {/* ── Section 1: Pending Profile Reviews ── */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '15px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
            Pending Profile Reviews ({practitioners.length})
          </h2>
          {practitioners.length > 0 ? (
            practitioners.map((p) => (
              <ProfileReviewCard key={p.id} practitioner={p} onAction={handleAction} actionLoading={actionLoading} />
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', background: 'white', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
              <p style={{ fontSize: '13px', color: '#9CA3AF' }}>No pending profiles.</p>
            </div>
          )}
        </div>

        {/* ── Section 2: Flagged Reviews ── */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '15px', fontWeight: 600, color: '#374151', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Flag size={16} style={{ color: '#D97706' }} />
            Flagged Reviews ({flaggedNotifs.length})
          </h2>
          {flaggedNotifs.length > 0 ? (
            flaggedNotifs.map((n) => (
              <FlaggedReviewCard key={n.id} notification={n} onAction={handleAction} actionLoading={actionLoading} />
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', background: 'white', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
              <p style={{ fontSize: '13px', color: '#9CA3AF' }}>No flagged reviews.</p>
            </div>
          )}
        </div>

        {/* ── Section 3: Quality Alerts ── */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '15px', fontWeight: 600, color: '#374151', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AlertTriangle size={16} style={{ color: '#D97706' }} />
            Quality Alerts ({qualityNotifs.length})
          </h2>
          {qualityNotifs.length > 0 ? (
            qualityNotifs.map((n) => (
              <QualityAlertCard key={n.id} notification={n} onDismiss={handleDismissAlert} actionLoading={actionLoading} />
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', background: 'white', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
              <p style={{ fontSize: '13px', color: '#9CA3AF' }}>No quality alerts.</p>
            </div>
          )}
        </div>

        {/* Refresh */}
        <div style={{ textAlign: 'center', marginTop: '8px', paddingBottom: '32px' }}>
          <span
            role="button" tabIndex={0}
            onClick={loadAllData}
            onKeyDown={(e) => e.key === 'Enter' && loadAllData()}
            style={{ fontSize: '13px', color: '#7c3aed', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
          >
            Refresh all
          </span>
        </div>
      </div>
    </div>
  );
};

export default AdminReviewQueue;
